import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentTools, type ToolCall, type ToolCallSummary } from '@/features/types/tools';
import { ToolCallContainer } from '../../ToolCallContainer';
import { ToolCallDataList } from '../../ToolCallDataList';
import { useToolCallParser, ToolCallState } from '../../useToolCallParser';
import { type ParsedSuccess } from '../../tool-call-utils';
import { ToolCallError } from '../../ToolCallError';
import { MeetingCard } from './common/MeetingCard';
import { useUserTimezone } from '@/hooks/useUserTimezone';

interface SearchEngagementsTool {
  name: AgentTools.chorus_search_engagements;
  args: {
    subject: string;
    limit?: number;
    includeSummary: boolean;
    includeMetrics: boolean;
    includeParticipants: boolean;
  };
}

export type SearchEngagementsToolCall = ToolCall<
  SearchEngagementsTool['name'],
  SearchEngagementsTool['args']
>;

export type SearchEngagementsToolCallSummary = ToolCallSummary<
  SearchEngagementsTool['name'],
  SearchEngagementsTool['args']
>;

interface SearchEngagementParticipant {
  company_name: string;
  email: string;
  name: string;
  person_id: number;
  title: string;
  type: string;
  user_id: number;
}

interface SearchEngagementsResult {
  continuation_key: string;
  count: boolean;
  engagements: Array<{
    account: { name: string };
    date_time: number;
    id: string;
    participants?: SearchEngagementParticipant[];
    subject: string;
    url: string;
  }>;
}

export function SearchEngagementsToolCall({
  toolCall,
}: {
  toolCall: SearchEngagementsToolCallSummary;
}) {
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation('prince');
  const userTimezone = useUserTimezone();

  const { parsedResult, title } = useToolCallParser<SearchEngagementsResult>(
    toolCall,
    ({ toolCallState, parsedResult }) => {
      switch (toolCallState) {
        case ToolCallState.Running:
          return t('toolCalls.chorus_search_engagements.title.whileRunning');
        case ToolCallState.Error:
          return t('toolCalls.chorus_search_engagements.title.errors');
        case ToolCallState.Success: {
          const successParsedResult = parsedResult as ParsedSuccess<SearchEngagementsResult>;
          return t('toolCalls.chorus_search_engagements.title.success', {
            count: successParsedResult?.results?.engagements?.length || 0,
          });
        }
      }
    }
  );

  const getFormattedMeetingDate = useCallback(
    (date: number) =>
      tCommon('format.date', {
        date: new Date(date * 1_000),
        formatParams: {
          date: {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            timezone: userTimezone,
          },
        },
      }),
    [tCommon, userTimezone]
  );

  const tabs = [
    {
      id: 'results',
      label: t('toolCalls.results'),
      showLoadingWhileRunning: true,
      content: parsedResult ? (
        !parsedResult.hasErrors ? (
          <div className="flex flex-col gap-3">
            {parsedResult.results.engagements.map(engagement => (
              <MeetingCard
                key={engagement.id}
                subject={engagement.subject}
                formattedDate={getFormattedMeetingDate(engagement.date_time)}
                participants={(engagement.participants || []).map(participant => ({
                  id: participant.person_id?.toString() || '',
                  name: participant.name,
                }))}
                accountName={engagement.account?.name || ''}
                url={engagement.url}
              />
            ))}
          </div>
        ) : (
          <ToolCallError parsedResult={parsedResult} />
        )
      ) : null,
    },
    {
      id: 'parameters',
      label: t('toolCalls.parameters'),
      content: (
        <ToolCallDataList
          items={[
            {
              label: t('toolCalls.chorus_search_engagements.parameters.subject'),
              value: toolCall.args.subject,
            },
            ...(toolCall.args.limit
              ? [
                  {
                    label: t('toolCalls.chorus_search_engagements.parameters.limit'),
                    value: toolCall.args.limit?.toString() || '',
                  },
                ]
              : []),
            {
              label: t('toolCalls.chorus_search_engagements.parameters.includeSummary'),
              value: toolCall.args.includeSummary ? tCommon('common.yes') : tCommon('common.no'),
            },
            {
              label: t('toolCalls.chorus_search_engagements.parameters.includeMetrics'),
              value: toolCall.args.includeMetrics ? tCommon('common.yes') : tCommon('common.no'),
            },
            {
              label: t('toolCalls.chorus_search_engagements.parameters.includeParticipants'),
              value: toolCall.args.includeParticipants
                ? tCommon('common.yes')
                : tCommon('common.no'),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <ToolCallContainer
      toolName={toolCall.name}
      title={title}
      isRunning={toolCall.isRunning}
      tabs={tabs}
    />
  );
}
